import {
	FREE_VEND_QUOTA_MAX,
	FREE_VEND_QUOTA_MIN,
	FREE_VEND_SALE_TYPE
} from '../_constants/free-vend.constants';

function toInteger(value: number | string | null | undefined): number | null {
	if (typeof value === 'number' && isFinite(value) && Math.floor(value) === value) {
		return value;
	}
	if (typeof value === 'string' && value.trim() !== '' && /^-?\d+$/.test(value.trim())) {
		return Number(value.trim());
	}
	return null;
}

export function consumedFromReported(
	reportedQuota: number | string | null | undefined,
	reportedRemaining: number | string | null | undefined
): number | null {
	const quota = toInteger(reportedQuota);
	const remaining = toInteger(reportedRemaining);
	if (quota === null || remaining === null) {
		return null;
	}
	return Math.max(0, quota - remaining);
}

export function validateFreeVendEnableQuota(
	quota: number | string | null | undefined,
	reportedQuota?: number | string | null,
	reportedRemaining?: number | string | null
): string | null {
	const parsedQuota = toInteger(quota);
	if (parsedQuota === null) {
		return 'Quota is required when enabling free mode.';
	}
	if (parsedQuota < FREE_VEND_QUOTA_MIN || parsedQuota > FREE_VEND_QUOTA_MAX) {
		return `Quota must be between ${FREE_VEND_QUOTA_MIN} and ${FREE_VEND_QUOTA_MAX}.`;
	}
	const consumed = consumedFromReported(reportedQuota, reportedRemaining);
	if (consumed !== null && parsedQuota < consumed) {
		return 'Quota is below drinks already consumed. Disable free mode instead.';
	}
	return null;
}

const VOUCHER_PAY_TYPE = 'voucher';

export function parseSaleTypeLabel(code: number | string | null | undefined): string {
	const parsed = toInteger(code);
	if (parsed === 1) {
		return 'Retail';
	}
	if (parsed === FREE_VEND_SALE_TYPE) {
		return 'Free';
	}
	return 'Office';
}

export function parsePayTypeLabel(payType: string | null | undefined): string {
	const normalized = String(payType || '').trim().toLowerCase();
	if (normalized === VOUCHER_PAY_TYPE) {
		return 'Voucher';
	}
	if (normalized === 'card') {
		return 'Card';
	}
	if (normalized === 'free') {
		return 'Free';
	}
	if (normalized === 'qr') {
		return 'QR';
	}
	if (!normalized) {
		return '—';
	}
	return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function parseDrinkSaleLabel(
	saleType: number | string | null | undefined,
	payType: string | null | undefined,
	showFreeVendLabel: boolean
): string {
	if (String(payType || '').trim().toLowerCase() === VOUCHER_PAY_TYPE) {
		return 'Voucher';
	}
	if (showFreeVendLabel) {
		return parseSaleTypeLabel(saleType);
	}
	const parsed = toInteger(saleType);
	if (parsed === 1) {
		return 'Retail';
	}
	return 'Office';
}

export function formatFreeVendProgress(
	drinkNumber: number | string | null | undefined,
	quota: number | string | null | undefined
): string {
	const parsedDrinkNumber = toInteger(drinkNumber);
	const parsedQuota = toInteger(quota);
	if (parsedDrinkNumber === null || parsedQuota === null) {
		return '—';
	}
	return `${parsedDrinkNumber} of ${parsedQuota}`;
}

export function serializeDesiredFreeVend(
	enabled: boolean,
	quota: number | string | null | undefined
): string {
	return JSON.stringify({
		enabled: !!enabled,
		quota: toInteger(quota)
	});
}

export function shouldSyncDesiredFreeVend(
	savedSnapshot: string | null | undefined,
	currentEnabled: boolean,
	currentQuota: number | string | null | undefined
): boolean {
	if (savedSnapshot == null || savedSnapshot === '') {
		return true;
	}
	return savedSnapshot === serializeDesiredFreeVend(currentEnabled, currentQuota);
}
